import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/**
 * Friendly "sanity check" before firing an admin email.
 * Shows a modal → user confirms → a 5-second undo toast → the send fires.
 *
 * Props:
 *   open        — controls visibility
 *   onClose     — cancel handler
 *   title       — modal heading (e.g. "Send report-ready email?")
 *   recipient   — string, shown in a highlighted row
 *   subject     — string
 *   bodyPreview — array of paragraph strings, or a single string
 *   attachments — array of strings (labels)
 *   undoSeconds — grace period (default 5)
 *   onSend      — async () => any — actual send fires after undo window
 *   confirmLabel— override the primary button label
 */
export default function EmailPreviewModal({
  open,
  onClose,
  title = "Send email?",
  recipient,
  subject,
  bodyPreview = [],
  attachments = [],
  undoSeconds = 5,
  onSend,
  confirmLabel = "Send now",
}) {
  const [pending, setPending] = useState(false);
  const cancelledRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      cancelledRef.current = false;
      setPending(false);
      if (timerRef.current) clearTimeout(timerRef.current);
    }
  }, [open]);

  if (!open) return null;

  const bodyParts = Array.isArray(bodyPreview) ? bodyPreview : [bodyPreview];

  const startSend = () => {
    if (pending) return;
    setPending(true);
    cancelledRef.current = false;
    onClose && onClose();

    const toastId = toast.custom(
      (id) => (
        <UndoToast
          seconds={undoSeconds}
          label={`Sending in {n}s — ${recipient || "recipient"}`}
          onUndo={() => {
            cancelledRef.current = true;
            if (timerRef.current) clearTimeout(timerRef.current);
            toast.dismiss(id);
            toast.info("Send cancelled.");
          }}
        />
      ),
      { duration: (undoSeconds + 1) * 1000 }
    );

    timerRef.current = setTimeout(async () => {
      if (cancelledRef.current) return;
      toast.dismiss(toastId);
      try {
        await onSend();
      } catch (e) {
        toast.error(e?.response?.data?.detail || e?.message || "Send failed.");
      } finally {
        setPending(false);
      }
    }, undoSeconds * 1000);
  };

  return (
    <div
      data-testid="email-preview-backdrop"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 50,
        background: "rgba(27, 26, 26, 0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        data-testid="email-preview-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#FFF", borderRadius: 20, maxWidth: 560, width: "100%",
          border: "1.5px solid var(--folk-border)",
          boxShadow: "0 24px 48px rgba(27, 26, 26, 0.25)",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "22px 24px 14px", borderBottom: "1.5px solid var(--folk-border)" }}>
          <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 700 }}>Preview & send</div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.028em", fontSize: 26, textTransform: "lowercase", marginTop: 4 }}>
            {title.toLowerCase()}
          </div>
        </div>

        <div style={{ padding: "18px 24px", display: "flex", flexDirection: "column", gap: 12, fontFamily: "Host Grotesk, sans-serif" }}>
          <PreviewRow label="To">
            <span data-testid="email-preview-recipient" style={{ fontWeight: 600 }}>{recipient || <span style={{ color: "var(--folk-hint)" }}>No email captured</span>}</span>
          </PreviewRow>
          <PreviewRow label="Subject">
            <span data-testid="email-preview-subject">{subject}</span>
          </PreviewRow>
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 700, marginBottom: 6 }}>Body</div>
            <div data-testid="email-preview-body" style={{ background: "var(--folk-bg)", border: "1.5px solid var(--folk-border)", borderRadius: 12, padding: 14, fontSize: 13.5, lineHeight: 1.55, color: "#2A2828", display: "flex", flexDirection: "column", gap: 8 }}>
              {bodyParts.filter(Boolean).map((p, i) => (<p key={i} style={{ margin: 0 }}>{p}</p>))}
            </div>
          </div>
          {attachments && attachments.length > 0 && (
            <div>
              <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 700, marginBottom: 6 }}>Attachments</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {attachments.map((a, i) => (
                  <span key={i} data-testid={`email-preview-attachment-${i}`} style={{ fontSize: 11.5, padding: "4px 10px", borderRadius: 999, background: "#FFFBC9", color: "#68280E", border: "1.5px solid rgba(104, 40, 14, 0.3)" }}>
                    {a}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div style={{ fontSize: 12, color: "var(--folk-muted)", background: "#BADDF0", border: "1.5px solid rgba(15, 58, 84, 0.25)", borderRadius: 10, padding: "8px 12px", lineHeight: 1.5 }}>
            You'll have {undoSeconds} seconds to undo after you hit send.
          </div>
        </div>

        <div style={{ padding: "14px 24px 22px", display: "flex", justifyContent: "flex-end", gap: 10, borderTop: "1.5px solid var(--folk-border)" }}>
          <button
            type="button"
            onClick={onClose}
            data-testid="email-preview-cancel"
            className="folk-pill"
            style={{ padding: "9px 16px", fontSize: 13, background: "transparent", color: "#2A2828", border: "1.5px solid var(--folk-border)" }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={startSend}
            disabled={pending || !recipient}
            data-testid="email-preview-send"
            className="folk-pill"
            style={{ padding: "9px 18px", fontSize: 13, background: "#68280E", color: "#FFF", border: "1.5px solid #68280E", opacity: (pending || !recipient) ? 0.55 : 1 }}
          >
            {pending ? "Queued…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewRow({ label, children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "80px 1fr", gap: 8, alignItems: "baseline" }}>
      <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 13.5, color: "#2A2828" }}>{children}</div>
    </div>
  );
}

/** Live countdown toast with an Undo button. Rendered inside a sonner custom toast. */
function UndoToast({ seconds, onUndo, label }) {
  const [n, setN] = useState(seconds);
  useEffect(() => {
    if (n <= 0) return;
    const iv = setInterval(() => setN((v) => v - 1), 1000);
    return () => clearInterval(iv);
  }, [n]);
  const text = String(label).replace("{n}", String(Math.max(0, n)));
  return (
    <div
      data-testid="email-undo-toast"
      style={{
        display: "flex", alignItems: "center", gap: 12,
        background: "#1B1A1A", color: "#FFFBC9",
        padding: "10px 14px", borderRadius: 12,
        fontFamily: "Host Grotesk, sans-serif", fontSize: 13,
        border: "1.5px solid #FFFBC9",
      }}
    >
      <span>{text}</span>
      <button
        type="button"
        onClick={onUndo}
        data-testid="email-undo-btn"
        style={{
          background: "#FFFBC9", color: "#68280E", border: "none",
          borderRadius: 999, padding: "5px 12px",
          fontSize: 12, fontWeight: 700, cursor: "pointer",
        }}
      >
        Undo
      </button>
    </div>
  );
}
